import { RadioButtonWithLabelGroupWidget, RadioButtonWithLabelOption } from '@tableau/widgets';
import { Formik, FormikProps, useFormikContext } from 'formik';
import React from 'react';
import _ from 'underscore';

import { FormikCheckbox } from '~commonInfra/components/FormikCheckbox';
import { FeatureFlags } from '~commonInfra/featureFlags/FeatureFlags';
import { SessionSiteSelectors } from '~src/bootstrap/redux/selectors/SessionInfoSelectors';
import { getAppStore } from '~src/bootstrap/redux/store/AppStore';
import { useSettingsCoordinator } from '~src/settings/components/settings-coordinator/SettingsCoordinatorHook';
import { ISiteEmailCustomizationSettings } from '~src/settings/types/ISiteSettingsForSiteAdmin';
import { SMTPAuthType} from '~src/settings/types/ISiteSettingsForSiteAdmin';

import { Messages } from '../../../../../codegen/Localize';
import { SettingsSection } from '../../../../components/common/SettingsSection';
import { componentRegistryEntry } from '../../SettingsComponentRegistryFunctions';
import { SiteSettingsForSiteAdminProps } from '../../site-settings/ReactSiteSettingsForSiteAdmin';
import { CredentialsEditorState, EmailCustomizationOptions } from './EmailCustomizationOptions';
import { ExternalSMTPConfiguration } from './serverApi/SiteSMTPCredential';

export const REMOVE_SUBSCRIPTION_AND_DDA_EMAIL_LINKS_FIELD = 'settings.subscriptionAndDDAEmailLinksEnabled';
export const EMAIL_CUSTOMIZATION_ENABLED_FIELD = 'settings.emailCustomizationSettings.isEmailCustomizationEnabled';
export const EMAIL_CUSTOMIZATION_SETTINGS_SECTION_TEST_ID = 'email-customization-settings-section';
export const EMAIL_SERVER_SETTINGS_SUBSECTION_TEST_ID = 'email-server-settings-subsection';
export const EMAIL_CUSTOMIZATION_ENABLE_OPTION_TEST_ID = 'email-customization-on-radio';
export const EMAIL_CUSTOMIZATION_DISABLE_OPTION_TEST_ID = 'email-customization-off-radio';
export const NOTIFICATION_EMAIL_SETTINGS_SUBSECTION_TEST_ID = 'notification-email-settings-subsection';
export const REMOVE_SUBSCRIPTION_AND_DDA_EMAIL_LINKS_CHECKBOX_TEST_ID = 'remove-subscription-and-dda-email-links-enabled';
export const EMAIL_CUSTOMIZATION_FORM_CONTENT_TEST_ID = 'email-customization-form-content';

export type EmailCustomizationSettingsSectionProps = SiteSettingsForSiteAdminProps<'subscriptionAndDDAEmailLinksEnabled' | 'emailCustomizationSettings'>;

function getButtonsList(props: EmailCustomizationFormContentProps): RadioButtonWithLabelOption<boolean>[] {
  return [
    {
      value: false,
      label: Messages.emailServerCustomizeOff(),
      testId: EMAIL_CUSTOMIZATION_DISABLE_OPTION_TEST_ID,
    },
    {
      value: true,
      label: Messages.emailServerCustomizeOn(),
      detail: <EmailCustomizationOptions {...props} onSave={props.onSave} />,
      testId: EMAIL_CUSTOMIZATION_ENABLE_OPTION_TEST_ID,
    }
  ];
}

const clearValues: EmailCustomizationFormFields = {
  isCustomizationEnabled: false,
  domain: '',
  username: '',
  friendlyName: '',
  host: '',
  localNameOrEmail: '',
  password: '',
  port: 587,
  authType: SMTPAuthType.BASIC,
  tenantId: '',
  clientId: '',
  clientSecret: '',
};

/**
 * This site setting is not hooked up directly through the larger site setting formik because updating site settings is handled
 * via individual APIs, `setSiteSMTPCredential` and `deleteSiteSMTPCredential`. These APIs update both site settings and the
 * credentials service where the SMTP password is stored.
 *
 * This site setting has its own little formik form to handle the email customization radio and credential inputs. Since this is
 * a child form of the parent site settings formik, the dirty state, valid state, and more are reflected in the top level save
 * enabled state. The links enabled checkbox, though in included in this setting section, is still handled the usual settings way.
 *
 * Turning on email customization occurs by submitting credentials for the custom SMTP server (click "Save" on credentials form).
 * Turning off email customization occurs by setting the radio back to default and clicking the top level save (this calls the
 * `handleSubmit` in this component).
 */
export function EmailCustomizationSettingsSection(props: EmailCustomizationSettingsSectionProps): React.ReactElement {
  const [emailCustomizationSettings, setEmailCustomizationSettings] = React.useState<ISiteEmailCustomizationSettings>(props.settings.emailCustomizationSettings);

  const initialValues: EmailCustomizationFormFields = emailCustomizationSettings ? {
    isCustomizationEnabled: emailCustomizationSettings.isEmailCustomizationEnabled,
    host: emailCustomizationSettings.emailCustomizationSMTPSUrl,
    domain: emailCustomizationSettings.emailCustomizationDomain,
    port: emailCustomizationSettings.emailCustomizationSMTPSPort,
    username: '',
    localNameOrEmail: emailCustomizationSettings.emailCustomizationSenderLocalNameOrEmail,
    friendlyName: emailCustomizationSettings.emailCustomizationSenderFriendlyName,
    password: '',
    authType: emailCustomizationSettings.emailCustomizationSMTPAuthType,
    tenantId: emailCustomizationSettings.emailCustomizationTenantId,
    clientId: emailCustomizationSettings.emailCustomizationClientId,
    clientSecret: '',
  } : clearValues;

  const handleSave = (values: EmailCustomizationFormFields) => {
    const emailCustomizationSettings: ISiteEmailCustomizationSettings = {
      isEmailCustomizationEnabled: values.isCustomizationEnabled,
      emailCustomizationDomain: values.domain,
      emailCustomizationSenderFriendlyName: values.friendlyName,
      emailCustomizationSMTPSPort: values.port,
      emailCustomizationSenderLocalNameOrEmail: values.localNameOrEmail,
      emailCustomizationSMTPSUrl: values.host,
      emailCustomizationClientId: values.clientId,
      emailCustomizationTenantId: values.tenantId,
      emailCustomizationSMTPAuthType: values.authType
    };
    setEmailCustomizationSettings(emailCustomizationSettings);
  };

  const handleSubmit = async (values: EmailCustomizationFormFields, formik: FormikProps<EmailCustomizationFormFields>) => {
    if(!values.isCustomizationEnabled && !!values.host) {
      await ExternalSMTPConfiguration.deleteSiteSMTPCredential(SessionSiteSelectors.siteLuid(getAppStore().getState()));

      handleSave(clearValues);
      formik.resetForm({ values: clearValues });
    }
  };

  const validate = (values: EmailCustomizationFormFields) => {
    let errors: EmailCustomizationFieldErrors = {
      isCustomizationEnabled: '',
      host: '',
      port: '',
      password: '',
      friendlyName: '',
      domain: '',
      username: ''
    };
    // handle most of the validation in child component and invalidate here if we are newly turning on the field and there are required fields missing
    const isRequiredFieldEmpty = _.isEmpty(values.host) || !values.port || _.isEmpty(values.password) || _.isEmpty(values.domain) || _.isEmpty(values.username);
    if (!emailCustomizationSettings.isEmailCustomizationEnabled && values.isCustomizationEnabled && isRequiredFieldEmpty) {
      // this string isn't currently user facing (not localized)
      errors.isCustomizationEnabled = 'You must add all required server credentials to enable this setting';
    }
    return errors.isCustomizationEnabled.length > 0 ? errors : null;
  };

  if (!FeatureFlags.emailCustomizationEnabled) {
    return null;
  }

  return (
    <>
      <SettingsSection
        title={Messages.emailCustomizationSettingTitle()}
        description={Messages.emailCustomizationSettingDescription()}
        testId={EMAIL_CUSTOMIZATION_SETTINGS_SECTION_TEST_ID}>
        <div style={{paddingBottom: 12}} data-tb-test-id={EMAIL_SERVER_SETTINGS_SUBSECTION_TEST_ID}>
          <Formik initialValues={initialValues}
            validate={validate}
            enableReinitialize={true}
            onSubmit={(values: EmailCustomizationFormFields, formik: FormikProps<EmailCustomizationFormFields>) => handleSubmit(values, formik)}>
              <EmailCustomizationFormContent {...props} onSave={handleSave} />
          </Formik>
        </div>
        {<div data-tb-test-id={NOTIFICATION_EMAIL_SETTINGS_SUBSECTION_TEST_ID}>
          <FormikCheckbox
            label={Messages.removeSubscriptionAndDDAEmailLinksDescription()}
            name={REMOVE_SUBSCRIPTION_AND_DDA_EMAIL_LINKS_FIELD}
            testId={REMOVE_SUBSCRIPTION_AND_DDA_EMAIL_LINKS_CHECKBOX_TEST_ID} />
        </div>}
      </SettingsSection>
    </>
  );
}

export interface EmailCustomizationFormFields extends Omit<CredentialsEditorState, 'port'> {
  isCustomizationEnabled: boolean;
  port: number;
}

export interface EmailCustomizationFormContentProps extends EmailCustomizationSettingsSectionProps {
  onSave: (values: EmailCustomizationFormFields) => void;
  clearCredentials?: boolean;
}

export const EmailCustomizationFormContent = (props: EmailCustomizationFormContentProps) => {
  useSettingsCoordinator();
  const emailCustFormik = useFormikContext<EmailCustomizationFormFields>();

  const buttons = React.useMemo(() => getButtonsList(props), [props]);
  const index = buttons.findIndex(button => button.value === emailCustFormik.values.isCustomizationEnabled);
  const handleChange = (value: boolean) => emailCustFormik.setFieldValue('isCustomizationEnabled', value);

  return <div data-tb-test-id={EMAIL_CUSTOMIZATION_FORM_CONTENT_TEST_ID} valid-form={emailCustFormik.isValid ? 'valid' : 'invalid'}>
    <RadioButtonWithLabelGroupWidget
      buttonList={buttons}
      aria-label={Messages.emailServerSetting()}
      checkedIndex={index}
      handleChange={handleChange}
      removeLeftMargin
    />
  </div>;
};

export const EmailCustomizationSettingsSectionSpecs = componentRegistryEntry({
  name: 'EmailCustomizationSettingsSection',
  component: EmailCustomizationSettingsSection,
});

interface EmailCustomizationFieldErrors {
  isCustomizationEnabled?: string;
  host?: string;
  port?: string;
  password?: string;
  friendlyName?: string;
  domain?: string;
  username?: string;
}